/* Rent vs Own House Calculator Logic — Integrates with common.js */

function calcEMI(principal, annualRate, years) {
  if (principal <= 0) return 0;
  const r = annualRate / 100 / 12;
  const n = years * 12;
  if (r === 0) return principal / n;
  return (principal * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
}

function fvSIP(pmt, annualRate, months) {
  if (months <= 0 || pmt <= 0) return 0;
  const r = annualRate / 100 / 12;
  if (r === 0) return pmt * months;
  return pmt * ((Math.pow(1 + r, months) - 1) / r);
}

function amortizeYear(loanBalance, emi, annualRate) {
  const r = annualRate / 100 / 12;
  let balance = loanBalance;
  let interestPaid = 0;
  let principalPaid = 0;

  for (let m = 0; m < 12; m++) {
    if (balance <= 0) break;
    const interest = balance * r;
    const principal = Math.min(emi - interest, balance);
    interestPaid += interest;
    principalPaid += Math.max(0, principal);
    balance -= Math.max(0, principal);
  }

  return { balance: Math.max(0, balance), interestPaid, principalPaid };
}

function getInputs() {
  const getVal = (id, def = 0) => {
    const el = document.getElementById(id);
    if (!el) return def;
    const v = +el.value;
    return isNaN(v) ? def : v;
  };

  const getCheck = (id) => {
    const el = document.getElementById(id);
    return el ? el.checked : false;
  };

  return {
    propertyPrice: getVal('propertyPrice', 10000000),
    downPaymentPct: getVal('downPayment', 20),
    buyingCostsPct: getVal('buyingCosts', 5),
    loanRate: getVal('loanRate', 8.5),
    loanTenure: getVal('loanTenure', 20),
    appreciation: getVal('appreciation', 6),
    maintenance: getVal('maintenance', 1),
    monthlyRent: getVal('monthlyRent', 30000),
    rentEscalation: getVal('rentEscalation', 7),
    equityReturn: getVal('equityReturn', 12),
    comparisonYears: getVal('comparisonYears', 20),
    includeTax: getCheck('includeTax'),
    taxRate: getVal('taxRate', 30),
    hraExemption: getVal('hraExemption', 0),
  };
}

function calculate(params) {
  const {
    propertyPrice, downPaymentPct, buyingCostsPct, loanRate, loanTenure,
    appreciation, maintenance, monthlyRent, rentEscalation, equityReturn,
    comparisonYears, includeTax, taxRate, hraExemption,
  } = params;

  const downPayment = propertyPrice * (downPaymentPct / 100);
  const buyingCosts = propertyPrice * (buyingCostsPct / 100);
  const loanAmount = propertyPrice - downPayment;
  const upfrontCash = downPayment + buyingCosts;
  const emi = calcEMI(loanAmount, loanRate, loanTenure);

  let homeValue = propertyPrice;
  let loanBalance = loanAmount;
  let renterPortfolio = upfrontCash;
  let currentRent = monthlyRent;
  let totalRentPaid = 0;
  let totalEmiPaid = 0;
  let totalMaintenance = 0;
  let totalTaxBenefitBuyer = 0;
  let totalTaxBenefitRenter = 0;
  let breakEvenYear = null;

  const schedule = [];

  for (let year = 1; year <= comparisonYears; year++) {
    homeValue *= 1 + appreciation / 100;

    const annualMaintenance = homeValue * (maintenance / 100);
    totalMaintenance += annualMaintenance;

    const { balance, interestPaid, principalPaid } = amortizeYear(loanBalance, emi, loanRate);
    loanBalance = balance;
    totalEmiPaid += emi * 12;

    const buyerNetWorth = homeValue - loanBalance;

    const monthlySip = Math.max(0, emi + annualMaintenance / 12 - currentRent);
    renterPortfolio = renterPortfolio * (1 + equityReturn / 100) + fvSIP(monthlySip, equityReturn, 12);

    const yearRent = currentRent * 12;
    totalRentPaid += yearRent;

    if (includeTax && typeof currentCountry !== 'undefined' && currentCountry.tax) {
      const { interestCap, principalCap } = currentCountry.tax;
      const interestDeduction = interestCap > 0 ? Math.min(interestPaid, interestCap) : interestPaid * 0.1;
      const principalDeduction = principalCap > 0 ? Math.min(principalPaid, principalCap) : 0;
      const buyerTaxSaving = (interestDeduction + principalDeduction) * (taxRate / 100);
      totalTaxBenefitBuyer += buyerTaxSaving;
      renterPortfolio += buyerTaxSaving;

      const renterTaxSaving = Math.min(hraExemption, yearRent) * (taxRate / 100);
      totalTaxBenefitRenter += renterTaxSaving;
      renterPortfolio += renterTaxSaving;
    }

    currentRent *= 1 + rentEscalation / 100;

    const advantage = renterPortfolio - buyerNetWorth;
    const winner = advantage >= 0 ? 'renter' : 'buyer';

    if (breakEvenYear === null && winner === 'buyer') {
      breakEvenYear = year;
    }

    schedule.push({
      year,
      homeValue,
      loanBalance,
      buyerNetWorth,
      yearRent,
      renterPortfolio,
      advantage,
      winner,
    });
  }

  const final = schedule[schedule.length - 1] || { renterPortfolio: 0, buyerNetWorth: 0, advantage: 0 };
  const netAdvantage = Math.abs(final.advantage);
  const overallWinner = final.advantage >= 0 ? 'renter' : 'buyer';

  return {
    emi,
    upfrontCash,
    downPayment,
    buyingCosts,
    loanAmount,
    totalRentPaid,
    totalEmiPaid,
    totalMaintenance,
    totalTaxBenefitBuyer,
    totalTaxBenefitRenter,
    breakEvenYear,
    schedule,
    final,
    netAdvantage,
    overallWinner,
    renterWealth: final.renterPortfolio,
    buyerWealth: final.buyerNetWorth,
  };
}

let chart = null;

function updateDisplays() {
  const p = getInputs();

  const setTxt = (id, txt) => {
    const el = document.getElementById(id);
    if (el) el.textContent = txt;
  };

  setTxt('propertyPriceDisplay', fmt(p.propertyPrice));
  setTxt('downPaymentDisplay', fmtPct(p.downPaymentPct));
  setTxt('buyingCostsDisplay', fmtPct(p.buyingCostsPct));
  setTxt('loanRateDisplay', fmtPct(p.loanRate));
  setTxt('loanTenureDisplay', `${p.loanTenure} yrs`);
  setTxt('appreciationDisplay', `${p.appreciation}%/yr`);
  setTxt('maintenanceDisplay', `${p.maintenance}%/yr`);
  setTxt('monthlyRentDisplay', fmt(p.monthlyRent));
  setTxt('rentEscalationDisplay', `${p.rentEscalation}%/yr`);
  setTxt('equityReturnDisplay', `${p.equityReturn}%/yr`);
  setTxt('comparisonYearsDisplay', `${p.comparisonYears} yrs`);
  setTxt('taxRateDisplay', fmtPct(p.taxRate));
  setTxt('hraExemptionDisplay', fmt(p.hraExemption));
  setTxt('renterYears', p.comparisonYears);
  setTxt('buyerYears', p.comparisonYears);
}

function renderResults(result, params) {
  try {
    const card = document.getElementById('verdictCard');
    if (card) {
      card.classList.remove('rent-wins', 'buy-wins');
      card.classList.add(result.overallWinner === 'renter' ? 'rent-wins' : 'buy-wins');
    }

    const badge = document.getElementById('verdictBadge');
    const headline = document.getElementById('verdictHeadline');
    const detail = document.getElementById('verdictDetail');

    if (badge) badge.textContent = result.overallWinner === 'renter' ? 'Rent & Invest wins' : 'Buying wins';
    if (headline) {
      headline.textContent = result.overallWinner === 'renter'
        ? `Investing beats buying by ${fmt(result.netAdvantage)}`
        : `Buying beats renting by ${fmt(result.netAdvantage)}`;
    }
    if (detail) {
      detail.textContent = result.overallWinner === 'renter'
        ? `Over ${params.comparisonYears} years, renting and investing your down payment plus monthly savings in equity builds more wealth than home equity.`
        : `Over ${params.comparisonYears} years, property appreciation and loan paydown create more net worth than renting and investing.`;
    }

    const setTxt = (id, txt) => {
      const el = document.getElementById(id);
      if (el) el.textContent = txt;
    };

    setTxt('netAdvantage', fmt(result.netAdvantage));
    setTxt(
      'breakEven',
      result.breakEvenYear
        ? `Year ${result.breakEvenYear}`
        : result.overallWinner === 'buyer' ? 'Already ahead' : 'Not within period'
    );
    setTxt('renterWealth', fmt(result.renterWealth));
    setTxt('buyerWealth', fmt(result.buyerWealth));

    const renterB = document.getElementById('renterBreakdown');
    if (renterB) {
      renterB.innerHTML = `
        <li><span>Initial investment</span><span>${fmt(result.upfrontCash)}</span></li>
        <li><span>Total rent paid</span><span>${fmt(result.totalRentPaid)}</span></li>
        <li><span>Tax benefits</span><span>${fmt(result.totalTaxBenefitRenter)}</span></li>
      `;
    }

    const buyerB = document.getElementById('buyerBreakdown');
    if (buyerB) {
      buyerB.innerHTML = `
        <li><span>Monthly EMI</span><span>${fmt(result.emi)}</span></li>
        <li><span>Down payment + fees</span><span>${fmt(result.upfrontCash)}</span></li>
        <li><span>Total EMI paid</span><span>${fmt(result.totalEmiPaid)}</span></li>
        <li><span>Maintenance & tax</span><span>${fmt(result.totalMaintenance)}</span></li>
        <li><span>Tax benefits</span><span>${fmt(result.totalTaxBenefitBuyer)}</span></li>
      `;
    }

    renderTable(result.schedule);

    try {
      if (typeof Chart !== 'undefined') {
        renderChart(result.schedule);
      }
    } catch (chartErr) {
      console.warn('Chart rendering error:', chartErr);
    }
  } catch (err) {
    console.error('renderResults error:', err);
  }
}

function renderChart(schedule) {
  const ctx = document.getElementById('wealthChart');
  if (!ctx) return;
  const labels = schedule.map((r) => `Yr ${r.year}`);
  const buyerData = schedule.map((r) => r.buyerNetWorth);
  const renterData = schedule.map((r) => r.renterPortfolio);

  if (chart) chart.destroy();

  chart = new Chart(ctx, {
    type: 'line',
    data: {
      labels,
      datasets: [
        {
          label: 'Buyer net worth',
          data: buyerData,
          borderColor: cssVar('--chart-buy'),
          backgroundColor: 'rgba(52, 211, 153, 0.08)',
          fill: true,
          tension: 0.3,
          pointRadius: 0,
          pointHoverRadius: 5,
          borderWidth: 2.5,
        },
        {
          label: 'Renter portfolio',
          data: renterData,
          borderColor: cssVar('--chart-rent'),
          backgroundColor: 'rgba(96, 165, 250, 0.08)',
          fill: true,
          tension: 0.3,
          pointRadius: 0,
          pointHoverRadius: 5,
          borderWidth: 2.5,
        },
      ],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: 'index', intersect: false },
      plugins: {
        legend: {
          position: 'top',
          align: 'end',
          labels: { color: cssVar('--chart-tick'), usePointStyle: true, pointStyle: 'circle', padding: 16 },
        },
        tooltip: {
          backgroundColor: cssVar('--tooltip-bg'),
          borderColor: cssVar('--tooltip-border'),
          borderWidth: 1,
          titleColor: cssVar('--tooltip-title'),
          bodyColor: cssVar('--tooltip-body'),
          callbacks: {
            label: (ctx) => `${ctx.dataset.label}: ${fmt(ctx.raw)}`,
          },
        },
      },
      scales: {
        x: {
          grid: { color: cssVar('--chart-grid') },
          ticks: { color: cssVar('--chart-tick'), maxTicksLimit: 10 },
        },
        y: {
          grid: { color: cssVar('--chart-grid') },
          ticks: {
            color: cssVar('--chart-tick'),
            callback: (v) => fmtCompact(v),
          },
        },
      },
    },
  });
}

function renderTable(schedule) {
  const tbody = document.getElementById('scheduleBody');
  if (!tbody) return;
  tbody.innerHTML = schedule.map((row) => {
    const advClass = row.winner === 'renter' ? 'advantage-renter' : 'advantage-buyer';
    const advLabel = row.winner === 'renter'
      ? `Renter +${fmt(Math.abs(row.advantage))}`
      : `Buyer +${fmt(Math.abs(row.advantage))}`;

    return `<tr>
      <td>Year ${row.year}</td>
      <td>${fmt(row.homeValue)}</td>
      <td>${fmt(row.loanBalance)}</td>
      <td>${fmt(row.buyerNetWorth)}</td>
      <td>${fmt(row.yearRent)}</td>
      <td>${fmt(row.renterPortfolio)}</td>
      <td class="${advClass}">${advLabel}</td>
    </tr>`;
  }).join('');
}

function exportCsv() {
  const params = getInputs();
  const result = calculate(params);
  const headers = ['Year', 'Home Value', 'Loan Balance', 'Buyer Net Worth', 'Rent (Year)', 'Renter Portfolio', 'Advantage'];
  const rows = result.schedule.map((r) => [
    r.year, Math.round(r.homeValue), Math.round(r.loanBalance),
    Math.round(r.buyerNetWorth), Math.round(r.yearRent),
    Math.round(r.renterPortfolio), Math.round(r.advantage),
  ]);

  const csv = [headers, ...rows].map((r) => r.join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = 'rent-vs-buy-schedule.csv';
  a.click();
  URL.revokeObjectURL(a.href);
}

function applySliderRange(id, range, value) {
  const el = document.getElementById(id);
  if (!el) return;
  el.min = range.min;
  el.max = range.max;
  el.step = range.step;
  el.value = value;
}

function applyCountryDefaults(country) {
  if (!country || !country.defaults || !country.ranges) return;
  const { defaults, ranges } = country;

  applySliderRange('propertyPrice', ranges.propertyPrice, defaults.propertyPrice);
  applySliderRange('monthlyRent', ranges.monthlyRent, defaults.monthlyRent);
  applySliderRange('hraExemption', ranges.hraExemption, defaults.hraExemption);

  if (document.getElementById('downPayment')) document.getElementById('downPayment').value = defaults.downPayment;
  if (document.getElementById('buyingCosts')) document.getElementById('buyingCosts').value = defaults.buyingCosts;
  if (document.getElementById('loanRate')) document.getElementById('loanRate').value = defaults.loanRate;
  if (document.getElementById('loanTenure')) document.getElementById('loanTenure').value = defaults.loanTenure;
  if (document.getElementById('appreciation')) document.getElementById('appreciation').value = defaults.appreciation;
  if (document.getElementById('maintenance')) document.getElementById('maintenance').value = defaults.maintenance;
  if (document.getElementById('rentEscalation')) document.getElementById('rentEscalation').value = defaults.rentEscalation;
  if (document.getElementById('equityReturn')) document.getElementById('equityReturn').value = defaults.equityReturn;
  if (document.getElementById('comparisonYears')) document.getElementById('comparisonYears').value = defaults.comparisonYears;
  if (document.getElementById('taxRate')) document.getElementById('taxRate').value = defaults.taxRate;
  if (document.getElementById('includeTax')) document.getElementById('includeTax').checked = false;
}

function updateCountryUI(country) {
  if (!country || !country.tax) return;
  const taxNote = document.getElementById('taxNote');
  if (taxNote) taxNote.textContent = country.tax.note;

  const taxRegimeLabel = document.getElementById('taxRegimeLabel');
  if (taxRegimeLabel) taxRegimeLabel.textContent = country.tax.regimeLabel;

  const hraLabel = document.getElementById('hraLabel');
  if (hraLabel) hraLabel.textContent = country.tax.hraLabel;
}

function recalculate() {
  updateDisplays();
  const params = getInputs();
  const result = calculate(params);
  renderResults(result, params);
}

function initRentVsBuy() {
  if (!document.getElementById('propertyPrice')) return; // Exit gracefully if not on rent-vs-buy page

  initCountry((country) => {
    applyCountryDefaults(country);
    updateCountryUI(country);
    recalculate();
  });

  if (typeof currentCountry !== 'undefined') {
    applyCountryDefaults(currentCountry);
    updateCountryUI(currentCountry);
  }

  const inputs = document.querySelectorAll('input[type="range"], input[type="checkbox"]');
  inputs.forEach((el) => el.addEventListener('input', recalculate));

  const exportBtn = document.getElementById('exportCsv');
  if (exportBtn) exportBtn.addEventListener('click', exportCsv);

  recalculate();
}

document.addEventListener('DOMContentLoaded', initRentVsBuy);
